import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { BulkUploadDetailStateModel } from './bulk-upload.models';
import { EmployeeApiService } from '../../_services/employee-service/api/employee-api.service';
import { interval, merge, Subject, takeUntil, tap, timer } from 'rxjs';
import { SetTimedOutState, StartPollingBulkUploadStatus, StopPollingBulkUploadStatus } from './bulk-upload.actions';

@State<BulkUploadDetailStateModel>({
  name: 'bulkUpload',
  defaults: {
    status: undefined,
    id: 0,
    timeout: false,
  },
})
@Injectable()
export class BulkUploadState {
  private readonly employeeApiService = inject(EmployeeApiService);
  readonly store = inject(Store);

  private readonly POLL_INTERVAL = 2000; // 2 second
  private readonly stopPolling$ = new Subject<void>();
  private readonly TIME_OUT = 60 * 1000; // 1 minute
  private readonly timeout$ = timer(this.TIME_OUT).pipe(
    tap(() => {
      this.store.dispatch(new SetTimedOutState());
    }),
  );

  // Combine both stop signals (manual stop and timeout)
  private readonly stopSignal$ = merge(this.stopPolling$, this.timeout$);

  @Selector()
  public static bulkUploadStatus(state: BulkUploadDetailStateModel): BulkUploadDetailStateModel {
    return {
      id: state.id,
      status: state.status,
      serverError: state.serverError,
      timeout: state.timeout,
    };
  }

  queryStatus(ctx: StateContext<BulkUploadDetailStateModel>, id: StartPollingBulkUploadStatus) {
    this.employeeApiService.getBulkEmployeeFile(id.id).subscribe({
      next: (data) => {
        ctx.patchState({ status: data.status });
        if (data.status !== 0) {
          this.stopPolling$.next();
        }
      },
      error: () => {
        ctx.patchState({ serverError: true });
        this.stopPolling$.next();
      },
    });
  }

  @Action(StartPollingBulkUploadStatus)
  startPollingBulkUploadStatus(ctx: StateContext<BulkUploadDetailStateModel>, id: StartPollingBulkUploadStatus) {
    ctx.patchState({ id: id.id, timeout: false });
    interval(this.POLL_INTERVAL)
      .pipe(takeUntil(this.stopSignal$))
      .subscribe(() => {
        this.queryStatus(ctx, id);
      });
  }

  @Action(StopPollingBulkUploadStatus)
  stopPollingBulkUploadStatus(ctx: StateContext<BulkUploadDetailStateModel>) {
    this.stopPolling$.next();
    ctx.patchState({ id: 0, status: undefined, serverError: false, timeout: false });
  }

  @Action(SetTimedOutState)
  setTimedOutState(ctx: StateContext<BulkUploadDetailStateModel>) {
    ctx.patchState({ timeout: true });
  }
}
